import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useABTest } from '../context/ABTestContext'
import { useClient } from '../context/ClientContext'
import { useTenant } from '../context/TenantContext'
import ABTestPopup from './ui/ABTestPopup'

/**
 * ABTestManager
 * Side-effect component that detects eligible AB test popup assignments and renders
 * the popup for EXISTING user segments only (existing_dormant_variant / existing_active_variant).
 *
 * new_* segments are handled by MyProfile.jsx directly after the user adds a card.
 *
 * Popup shows at most once per browser session per test, regardless of navigation.
 * Session key written to sessionStorage: ab_popup_shown_{externalUserId}_{testId}
 *
 * Placement: Inside ABTestProvider, runs globally across all routes.
 */

const ABTestManager = () => {
  const {
    assignments,
    isSessionDismissed,
    dismissForSession,
    isShownThisSession,
    markShownThisSession,
    crossTabVersion,
    acquireABTestLock,
    releaseABTestLock,
    isCurrentABTestTab,
    isABTestClaimOwner,
    isABTestHandledThisSession,
    markABTestHandledThisSession,
    startTour,
    tourActive,
  } = useABTest()
  const { client, isAuthenticated } = useClient()
  const { tenant } = useTenant()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [activeAssignment, setActiveAssignment] = useState(null)
  // Tracks which test IDs have already been shown in this app-lifetime mount
  // Using a ref Set (not sessionStorage) avoids re-triggering when sessionId changes
  const shownTestsRef = useRef(new Set())
  const isClosingRef = useRef(false)

  // Determine which assignment (if any) should show a popup
  useEffect(() => {
    if (tourActive) {
      setActiveAssignment(null)
      return
    }

    if (!isAuthenticated || !client?.externalUserId) {
      // Clear in-memory shown set so popup re-evaluates on next login
      shownTestsRef.current.clear()
      setActiveAssignment(null)
      return
    }

    // Guard: ab_test feature flag must be enabled in masterconfig
    if (!tenant?.tenantmasterconfig?.ab_test?.enabled) {
      setActiveAssignment(null)
      return
    }

    // Guard: current URL must belong to the authenticated user's tenant.
    // Prevents popup appearing on 404 / wrong-tenant pages.
    // Allow both the root tenant path (no trailing slash) and any sub-path.
    const tenantBase = `/${client.tenantUrlName}`
    if (client?.tenantUrlName && pathname !== tenantBase && !pathname.startsWith(`${tenantBase}/`)) {
      setActiveAssignment(null)
      return
    }

    const eligible = assignments.find((a) => {
      // All variant segments: new_variant, existing_dormant_variant, existing_active_variant
      if (!a.segment_type?.endsWith('_variant')) return false
      // Skip if popup content is not configured — ABTestPopup returns null when content is missing,
      // and we must NOT mark it as shown or it will be permanently suppressed for the session.
      if (!a.content) return false
      // Permanently discarded
      if (a.is_popup_discarded) return false
      // Tour already completed
      if (a.is_tour_completed) return false
      // Session-level suppression ("Maybe Later" clicked this session)
      if (isSessionDismissed(a.test_id)) return false
      // Cross-tab suppression after any popup action in this login session
      if (isABTestHandledThisSession(a.test_id)) return false
      // Already shown via MyProfile card-add flow this session
      if (isShownThisSession(a.test_id) && !isABTestClaimOwner(a.test_id)) return false
      // Already shown once in this app mount (prevents re-showing on navigation or assignments reload)
      if (shownTestsRef.current.has(a.test_id)) return false

      // new_variant: first show is handled by MyProfile after card add (trigger=card_enrollment)
      // or by ABTestManager on first login (trigger=login).
      // ABTestManager also re-shows on subsequent logins after "Maybe Later".
      // localStorage flag `ab_np_{externalUserId}_{testId}` is set by MyProfile on first show
      // when trigger=card_enrollment, so ABTestManager knows it's safe to re-show.
      if (a.segment_type === 'new_variant') {
        const newConfig = tenant?.tenantmasterconfig?.ab_test?.new
        if (newConfig?.enabled === false) return false
        if ((newConfig?.trigger ?? 'card_enrollment') === 'card_enrollment') {
          // First show is in MyProfile — only re-show here once MyProfile has shown it
          try {
            if (localStorage.getItem(`ab_np_${client.externalUserId}_${a.test_id}`) !== '1') return false
          } catch { return false }
        }
        // trigger=login: first (and subsequent) shows are all handled here — no localStorage gate
      } else {
        // existing_dormant_variant / existing_active_variant
        const isActive = a.segment_type.includes('_active_')
        const typeConfig = isActive
          ? tenant?.tenantmasterconfig?.ab_test?.existing_active
          : tenant?.tenantmasterconfig?.ab_test?.existing_dormant
        if (typeConfig?.enabled === false) return false
      }

      return true
    })

    if (eligible) {
      if (!acquireABTestLock(eligible.test_id, 'popup')) return
      // Mark in-memory + sessionStorage so subsequent renders and page refreshes won't re-show
      shownTestsRef.current.add(eligible.test_id)
      markShownThisSession(eligible.test_id)
      isClosingRef.current = false
      setActiveAssignment(eligible)
    }
  }, [assignments, isAuthenticated, client?.externalUserId, client?.tenantUrlName, tenant, pathname, tourActive, crossTabVersion, isSessionDismissed, isShownThisSession, isABTestClaimOwner, isABTestHandledThisSession, markShownThisSession, acquireABTestLock])

  useEffect(() => {
    if (!activeAssignment) return
    if (
      isABTestHandledThisSession(activeAssignment.test_id) ||
      !isCurrentABTestTab(activeAssignment.test_id)
    ) {
      setActiveAssignment(null)
    }
  }, [activeAssignment, crossTabVersion, isABTestHandledThisSession, isCurrentABTestTab])

  const handleClose = useCallback((action) => {
    if (isClosingRef.current) return
    isClosingRef.current = true

    if (action === 'Maybe Later') {
      dismissForSession(activeAssignment?.test_id)
    }

    if (action === 'View Tour') {
      const assignment = activeAssignment
      setActiveAssignment(null)
      window.setTimeout(() => {
        if (!startTour(assignment)) {
          markABTestHandledThisSession(assignment?.test_id)
          releaseABTestLock(assignment?.test_id)
          return
        }
        navigate(`/${client.tenantUrlName}/myprofile?tab=my-cards`)
      }, 0)
      return
    }

    markABTestHandledThisSession(activeAssignment?.test_id)
    releaseABTestLock(activeAssignment?.test_id)
    setActiveAssignment(null)
  }, [activeAssignment, client, dismissForSession, markABTestHandledThisSession, releaseABTestLock, startTour, navigate])

  if (!activeAssignment || tourActive) return null

  return (
    <ABTestPopup
      open={!!activeAssignment}
      assignment={activeAssignment}
      onClose={handleClose}
    />
  )
}

export default ABTestManager
